import { useEffect, useRef } from 'react';
import { fuego } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import { LevelSystem, PetInteractionLog, UserPet } from '@typings/pets/pet.interface';
import { updateUserWallet } from '@store/firebase/user/updateUser';
import useLevelCheck from './useLevelCheck';
import { WalletHistoryType } from '@store/firebase/user/addWalletHistoryByType';

type Params = {
  currentUid: string | null | undefined;
  activePet: UserPet | any | null | undefined;
  levelSystem?: LevelSystem | null;
  onLevelUp: (fromLevel: number, toLevel: number) => void;
  setUserPets: React.Dispatch<React.SetStateAction<any[]>>;
};

export default function useAutoLevelUp({ currentUid, activePet, levelSystem, onLevelUp, setUserPets }: Params) {
  const { computeLevelFromExp } = useLevelCheck(levelSystem);
  const levelingLockRef = useRef<boolean>(false);
  const activePetId = (activePet as any)?.id;

  useEffect(() => {
    (async () => {
      try {
        const docPath = currentUid && activePetId ? `${CollectionPath.USERS}/${currentUid}/pets/${activePetId}` : '';
        if (!activePet || !currentUid || !docPath || !activePetId) return;
        const currExp = Number((activePet as any).experience || 0);
        const prevLevel = Number((activePet as any).level || 1);
        const nextLevel = computeLevelFromExp(currExp);
        if (nextLevel > prevLevel && !levelingLockRef.current) {
          levelingLockRef.current = true;
          // 레벨/경험치 업데이트 (경험치 0으로 리셋)
          await fuego.db.doc(docPath).set({ level: nextLevel, experience: 0, updatedAt: new Date() } as any, { merge: true } as any);
          setUserPets((prev) => prev.map((p: any) => (p?.id === activePetId ? { ...p, level: nextLevel, experience: 0 } : p)));
          onLevelUp(prevLevel, nextLevel);

          // 레벨업 보상 지급
          if (Number((levelSystem as any)?.levelUpRewards?.[nextLevel]?.currency || 0) > 0 && currentUid) {
            await updateUserWallet({
              uids: [currentUid],
              price: Number((levelSystem as any)?.levelUpRewards?.[nextLevel]?.currency || 0),
              type: WalletHistoryType.PETLEVELUP,
              memo: activePet.customName || '나의 펫'
            });
          }

          // 레벨업 인터랙션 로그 추가 (로컬 + Firestore)
          const petName = (activePet as any)?.customName || '펫';
          const currencyAmount = Number((levelSystem as any)?.levelUpRewards?.[nextLevel]?.currency || 0);
          const newLog: PetInteractionLog = {
            id: `log_levelup_${Date.now()}`,
            userId: String(currentUid || ''),
            petId: (activePet as any)?.id,
            type: 'growth',
            message: `🎉 ${petName}이(가) 레벨 ${nextLevel}이 되었습니다! ${currencyAmount > 0 ? `[재화 + ${currencyAmount}]` : ''}`,
            createdAt: new Date(),
          } as any;

          if (currentUid && (activePet as any)?.id) {
            fuego.db
              .collection(`${CollectionPath.USERS}/${currentUid}/pets/${(activePet as any).id}/logs`)
              .add(newLog as any)
              .catch(() => { });
          }

          //  진화버튼을 눌렀는지 체크
          await fuego.db.doc(docPath).set({ evolutionReadyFor: '' } as any, { merge: true } as any);
          setTimeout(() => { levelingLockRef.current = false; }, 200);
        }
      } catch {
        levelingLockRef.current = false;
      }
    })();
  }, [activePet?.experience, activePet?.level, computeLevelFromExp, levelSystem, currentUid, activePetId, setUserPets, onLevelUp]);
}


